import { ProblemDetail } from 'src/common/ProblemDetail';

// Cloud Run rejects HTTP/1 request bodies over 32 MiB at the infrastructure level — the 413
// never reaches Spring and carries no CORS headers, so the browser only sees an opaque
// network error. Spring additionally caps multipart uploads at 40 MB. Stay below both,
// with headroom for the multipart envelope and the JSON form part.
export const MAX_UPLOAD_BYTES = 30 * 1024 * 1024;

export const UPLOAD_TOO_LARGE_ERROR_CODE = 'UPLOAD_TOO_LARGE';

export function formatMegabytes(bytes: number): string {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function uploadTooLargeProblem(detail: string): ProblemDetail {
    return {
        type: 'about:blank',
        title: 'Upload too large',
        status: 413,
        detail,
        instance: '',
        errorCode: UPLOAD_TOO_LARGE_ERROR_CODE,
    };
}

export class GaebUploadTooLargeError extends Error {
    constructor(file: File, maxBytes: number, imagesWereStripped: boolean) {
        super(
            imagesWereStripped
                ? `"${file.name}" is still ${formatMegabytes(file.size)} after removing embedded images — ` +
                      `the upload limit is ${formatMegabytes(maxBytes)}. ` +
                      'Reduce the file size in the GAEB authoring tool and try again.'
                : `"${file.name}" is ${formatMegabytes(file.size)}, but the upload limit is ` +
                      `${formatMegabytes(maxBytes)} and the file contains no embedded images that could be removed. ` +
                      'Reduce the file size in the GAEB authoring tool and try again.',
        );
        this.name = 'GaebUploadTooLargeError';
    }
}

/**
 * Returns the file to upload — unchanged when it is small enough. Oversized GAEB files are
 * shrunk by dropping all `<image>` elements (NOVA AVA exports embed base64 images in the
 * long texts, often >99% of the file size) — the backend GAEB parser never reads them.
 * The stripped copy is what gets uploaded and archived; the user's original stays on disk.
 * Throws {@link GaebUploadTooLargeError} when the file exceeds the limit even after stripping.
 */
export async function prepareGaebUpload(file: File, maxBytes: number = MAX_UPLOAD_BYTES): Promise<File> {
    if (file.size <= maxBytes) {
        return file;
    }
    const stripped = await stripEmbeddedImages(file);
    if (stripped.size > maxBytes) {
        throw new GaebUploadTooLargeError(stripped, maxBytes, stripped !== file);
    }
    return stripped;
}

async function stripEmbeddedImages(file: File): Promise<File> {
    if (!(await isUtf8Xml(file))) {
        return file;
    }
    const doc = new DOMParser().parseFromString(await file.text(), 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0) {
        return file;
    }
    // Match <image> in any namespace: GAEB 3.1/3.2/3.3 use different namespace URIs, and
    // some authoring tools duplicate content under a secondary backup namespace.
    const images = Array.from(doc.getElementsByTagNameNS('*', 'image'));
    if (images.length === 0) {
        return file;
    }
    for (const image of images) {
        image.parentNode?.removeChild(image);
    }
    const serialized = new XMLSerializer().serializeToString(doc);
    const xml = serialized.startsWith('<?xml') ? serialized : `<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`;
    return new File([xml], file.name, { type: file.type, lastModified: file.lastModified });
}

// File.text() always decodes as UTF-8; re-serializing a file that declares another
// encoding would corrupt its umlauts, so such files are passed through untouched.
// The leading '<' check rules out non-XML files (e.g. a PDF misrouted here) before we
// commit to reading and DOM-parsing the whole multi-MB body.
async function isUtf8Xml(file: File): Promise<boolean> {
    const head = (await file.slice(0, 200).text()).trimStart();
    if (!head.startsWith('<')) {
        return false;
    }
    const encoding = head.match(/encoding=["']([^"']+)["']/)?.[1];
    return encoding === undefined || /^utf-?8$/i.test(encoding);
}
